import React, { useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Country, State, City } from "country-state-city";
import styles from "./addAddress.module.css";
import { closeAddressModel } from "../../../Svg";
import { addShippingAddressThunk, editShippingAddressThunk } from "../../../redux/userSlice";
import { getActiveCountries } from "../../../redux/homeSlice";
import { useDispatch, useSelector } from "react-redux";

const AddAddress = ({ onClose, data, from }) => {

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { activeCountries } = useSelector((state) => state.appearances);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("firstName is required"),
    lastName: Yup.string().required("lastName is required"),
    country: Yup.string().required("Country is required"),
    // companyName: Yup.string().required("Company Name is required"),
    streetAddress: Yup.string().required("Address Line 1 is required"),
    unit: Yup.string(),
    email: Yup.string().email(),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    pincode: Yup.string()
      .required("Zip Code is required")
      .matches(/^\d{5,6}$/, "Invalid Zip Code"),
    phone: Yup
      .string()
      .matches(/^\d{10}$/, "Invalid phone number")
      .required("Phone number is required"),

  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
  } = useFormik({
    initialValues: {
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      country: data?.country || "",
      streetAddress: data?.streetAddress || "",
      unit: data?.unit || "",
      city: data?.city || "",
      state: data?.state || "",
      pincode: data?.pincode || "",
      phone: data?.phone || "",
      email: data?.email || "",
      landmark: data?.landmark || "",
      addressId: data?._id || ""
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      const status = from == 'add' ? await dispatch(addShippingAddressThunk(values)) : await dispatch(editShippingAddressThunk(values))
      if (status.payload == 200) {
        onClose()
      }
      setLoading(false)
    },
  });

  useEffect(() => {
    const getAllActiveCountries = async () => {
      try {
        await dispatch(getActiveCountries());
      } catch (error) {
        console.log(error);
      }
    };
    getAllActiveCountries();
  }, [dispatch]);


  const activeCountryNames = useMemo(() => {
    return activeCountries?.data?.map(country => country.name) || [];
  }, [activeCountries]);

  const countries = useMemo(() => {
    return Country.getAllCountries()
      .filter(country => activeCountryNames.includes(country.name))
      .map(country => ({
        label: country.name,
        value: country.isoCode,
        fullName: country.name,
      }));
  }, [activeCountryNames]);

  useEffect(() => {
    if (activeCountryNames.length > 0 && values.country && !activeCountryNames.includes(values.country)) {
      setFieldValue("country", "");
      setFieldValue("state", "");
      setFieldValue("city", "");
    }
  }, [activeCountryNames, values.country, setFieldValue]);

  const billingStates = useMemo(() => {
    if (!values.country) return [];
    const countryObj = Country.getAllCountries().find(c => c.name === values.country);
    return State.getStatesOfCountry(countryObj?.isoCode).map(state => ({
      label: state.name,
      value: state.isoCode,
    }));
  }, [values.country]);

  const billingCities = useMemo(() => {
    if (!values.state || !values.country) return [];
    const countryObj = Country.getAllCountries().find(c => c.name === values.country);
    const stateObj = State.getStatesOfCountry(countryObj?.isoCode).find(s => s.name === values.state);
    return City.getCitiesOfState(countryObj?.isoCode, stateObj?.isoCode).map(city => ({
      label: city.name,
      value: city.name,
    }));
  }, [values.country, values.state]);


  const handleBillingCountryChange = (e) => {
    const countryCode = e.target.value;
    const selectedCountry = countries.find((c) => c.value === countryCode);
    setFieldValue("country", selectedCountry?.fullName || ""); // Store country name
    setFieldValue("state", ""); // Reset state
    setFieldValue("city", "");  // Reset city
  };

  const handleBillingStateChange = (e) => {
    const stateCode = e.target.value;
    const selectedState = billingStates.find((s) => s.value === stateCode);
    setFieldValue("state", selectedState?.label || ""); // Store state name
    setFieldValue("city", ""); // Reset city
  };

  const handleBillingCityChange = (e) => {
    const cityCode = e.target.value;
    const selectedCity = billingCities.find(c => c.value === cityCode);
    setFieldValue("city", selectedCity?.label || "");
  };


  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popupContent}>
        <button className={styles.popupcloseButton} onClick={onClose}>
          {closeAddressModel()}
        </button>
        <div className={styles.popuptitle}>{from === 'add' ? 'Add New Address' : 'Edit Address'}</div>

        <form className={styles.qrForm} onSubmit={handleSubmit}>
          <div className={styles.hrLine}></div>
          <div className={styles.qrForminputMain}>
            <div className={styles.loginFormRow1}>
              <div className={styles.loginInputGroup2}>
                <label>First Name*</label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter first name"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.firstName && touched.firstName && (
                  <div className={styles.firstName}>{errors.firstName}</div>
                )}
              </div>
              <div className={styles.loginInputGroup2}>
                <label>Last Name*</label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Enter last name"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.lastName && touched.lastName && (
                  <div className={styles.lastName}>{errors.lastName}</div>
                )}
              </div>
            </div>
            <div className={styles.loginFormRow1}>
              <div className={styles.loginInputGroup2} >
                <label>Phone*</label>
                <input
                  type="text"
                  name="phone"
                  placeholder="Enter Phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.phone && touched.phone && (
                  <div className={styles.error}>{errors.phone}</div>
                )}
              </div>
              <div className={styles.loginInputGroup2} >
                <label>Email</label>
                <input
                  type="text"
                  name="email"
                  placeholder="Enter Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.email && touched.email && (
                  <div className={styles.error}>{errors.email}</div>
                )}
              </div>

            </div>

            <div className={styles.loginFormRow1}>
              <div className={styles.loginInputGroup2}>
                <label>Address Line 1 *</label>
                <input
                  type="text"
                  name="streetAddress"
                  placeholder="House/Flat No., Building Name"
                  value={values.streetAddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.streetAddress && touched.streetAddress && (
                  <div className={styles.error}>{errors.streetAddress}</div>
                )}
              </div>
              <div className={styles.loginInputGroup2}>
                <label>Address Line 2</label>
                <input
                  type="text"
                  name="unit"
                  placeholder="Street Name, Locality"
                  value={values.unit}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
              </div>
            </div>

            <div className={styles.loginFormRow2}>
              <div className={styles.loginInputGroup2}>
                <label>Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  placeholder="Enter Landmark"
                  value={values.landmark}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {/* {errors.companyName && touched.companyName && (
                  <div className={styles.error}>{errors.companyName}</div>
                )} */}
              </div>
              <div className={styles.loginInputGroup2}>
                <label>Country / Region*</label>
                <select
                  name="country"
                  className={styles.selectField}
                  value={countries.find(c => c.label === values.country)?.value || ""}
                  onChange={handleBillingCountryChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select</option>
                  {countries.length > 0 ? (
                    countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading countries...</option>
                  )}
                </select>

                {errors.country && touched.country && (
                  <div className={styles.error}>{errors.country}</div>
                )}
              </div>

            </div>

            <div className={styles.loginFormRow2}>

              <div className={styles.loginInputGroup}>
                <label>State*</label>
                <select
                  name="state"
                  className={styles.selectField}
                  value={billingStates.find(c => c.label === values.state)?.value || ""}
                  onChange={handleBillingStateChange}
                  onBlur={handleBlur}
                  disabled={!values.country}
                >
                  <option value="">Select</option>
                  {billingStates?.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
                {errors.state && touched.state && (
                  <div className={styles.error}>{errors.state}</div>
                )}
              </div>

              <div className={styles.loginInputGroup}>
                <label>Zip Code*</label>
                <input
                  type="text"
                  name="pincode"
                  placeholder="Enter Zip Code"
                  value={values.pincode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ color: '#1a1a1a' }}
                />
                {errors.pincode && touched.pincode && (
                  <div className={styles.error}>{errors.pincode}</div>
                )}
              </div>
              <div className={styles.loginInputGroup}>
                <label>City*</label>
                <select
                  name="city"
                  className={styles.selectField}
                  value={billingCities.find(c => c.label === values.city)?.value || ""}
                  onChange={handleBillingCityChange}
                  onBlur={handleBlur}
                  disabled={!values.state || !values.country}
                >
                  <option value="">Select</option>
                  {billingCities?.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </select>
                {errors.city && touched.city && (
                  <div className={styles.error}>{errors.city}</div>
                )}
              </div>

            </div>

            <button type="submit" className={styles.savechanges}>
              {loading ? "please wait..." : "Save Address"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddress;
